import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'
import { v4 as uuidv4 } from 'uuid'

import { useHybridClassVideoUploadStore } from '../../../../store/hybridclassvideoupload.store.js'
import VisuallyHiddenInput from '../../../../components/common/VisuallyHiddenInput'
import CustomSelectField from '../../../../components/common/CustomSelectField.js'
import { useOrganizationStore } from '../../../../store/organization.store.js'
import CustomChip from '../../../../components/common/CustomChip.js'
import { debounce, getFileSizeFromBytes, Icons } from '../../../../utils/utilities'
import RecordingFileCard from './RecordingFileCard.jsx'
import api from '../../../../service/api'
import { useSettingsStore } from '../../../../store/settings.store.js'

const ACCEPTED_VIDEO_TYPES = "video/mp4,video/quicktime,video/webm"
const DEFAULT_MAX_VIDEO_SIZE_MB = 2048

const createFileEntry = ( file, defaultSubject, defaultInstitute ) => {
    const dotIndex = file.name.lastIndexOf( "." )
    const localUrl = URL.createObjectURL( file )
    return {
        id: uuidv4(),
        file,
        name: dotIndex > 0 ? file.name.slice( 0, dotIndex ) : file.name,
        institute: defaultInstitute || null,
        subjects: defaultSubject ? defaultSubject : [],
        topics_covered: "",
        module_number: "",
        module_name: "",
        topic: "",
        frame: 25,
        custom: false,
        localUrl,
        customThumbUrl: null,
        customThumbFile: null,
        frameThumbBlobs: {},
        durationSeconds: null
    }
}

// Resolves whichever thumbnail the user ended up with (custom upload, or a captured video frame) into a real File
const loadImageMetadata = async ( blob ) => {
    try {
        const bitmap = await createImageBitmap( blob )
        const { width, height } = bitmap
        bitmap.close()
        return { width, height, aspectRatio: height ? width / height : null }
    } catch ( err ) {
        console.warn( "Failed to load thumbnail metadata:", err )
        return { width: null, height: null, aspectRatio: null }
    }
}

const buildThumbnailFile = async ( entry ) => {
    const file = entry.custom
        ? entry.customThumbFile || null
        : ( () => {
            const blob = entry.frameThumbBlobs?.[entry.frame]
            return blob ? new File( [blob], "thumbnail.jpg", { type: "image/jpeg" } ) : null
        } )()

    if ( !file ) return { file: null, metadata: {} }

    const metadata = {
        name: file.name,
        type: file.type,
        size: file.size,
        source: entry.custom ? "custom" : "frame",
        frame: entry.custom ? null : entry.frame,
        ...( await loadImageMetadata( file ) ),
    }

    return { file, metadata }
}

// ---- Add recording dialog ---------------------------------------------------

const AddRecordingDialog = observer( ( { onClose, modules, availableSubjects, getModuleNameMap } ) => {
    const [institute, setInstitute] = useState( { institute_name_short: 'AIT', institute_id: 1, institute_name: "ACHARYA INSTITUTE OF TECHNOLOGY" } )
    const [subjects, setSubjects] = useState( availableSubjects ?? [] )
    const [loadingSubjects, setLoadingSubjects] = useState( false )
    const [batchSubject, setBatchSubject] = useState( [] )
    const [dragOver, setDragOver] = useState( false )
    const [files, setFiles] = useState( [] )

    const OrganizationStore = useOrganizationStore()
    const SettingsStore = useSettingsStore()

    const { palette, border, layout } = useTheme()
    const UploadStore = useHybridClassVideoUploadStore()

    const MAX_VIDEO_SIZE_MB = useMemo( () => {

        const instituteMaxSize = SettingsStore?.getSettings?.[institute.institute_name_short]?.find( setting => setting.setting_key === "max_hybrid_class_video_size" )
        return +instituteMaxSize?.value || DEFAULT_MAX_VIDEO_SIZE_MB

    }, [SettingsStore?.getSettings, institute] )

    // Live subject/module -> module_name lookup, seeded from the repository's already-fetched recordings and
    // kept updated as module names are typed in this batch, so later cards can auto-fill off of earlier ones.
    const [moduleNameMap, setModuleNameMap] = useState( () => getModuleNameMap() )

    // Records a module name against every subject it applies to, and backfills it into any other selected
    // video that already has the same module picked but no name of its own yet.
    const commitModuleName = ( subjectCodes, module_number, module_name ) => {
        if ( !module_number || !module_name || !subjectCodes?.length ) return

        setModuleNameMap( prev => {
            const next = { ...prev }
            subjectCodes.forEach( code => {
                next[code] = { ...( next[code] || {} ), [module_number]: module_name }
            } )
            return next
        } )

        setFiles( prev => prev.map( entry => {
            if ( entry.module_number !== module_number || entry.module_name ) return entry
            const entrySubjectCodes = ( entry.subjects || [] ).map( s => s.subject_code )
            const matches = entrySubjectCodes.some( code => subjectCodes.includes( code ) )
            return matches ? { ...entry, module_name } : entry
        } ) )
    }

    const searchSubjects = async ( searchText ) => {
        const { data } = await api.audit.fetchSubjects( { subject_code: searchText, institute: institute?.institute_name_short } )
        return data?.data || []
    }

    const patchFile = ( id, patch ) => {
        setFiles( prev => prev.map( entry => entry.id === id ? { ...entry, ...patch } : entry ) )
    }
    const removeFile = ( id ) => setFiles( prev => prev.filter( entry => entry.id !== id ) )

    const loadDuration = ( id, file ) => {
        const url = URL.createObjectURL( file )
        const videoEl = document.createElement( "video" )
        videoEl.preload = "metadata"
        videoEl.onloadedmetadata = () => {
            patchFile( id, { durationSeconds: videoEl.duration } )
            URL.revokeObjectURL( url )
        }
        videoEl.onerror = () => URL.revokeObjectURL( url )
        videoEl.src = url
    }

    const handleInstituteChange = ( event ) => {
        const inst = OrganizationStore.getOrganization?.find( i => i.institute_name_short === event.target.value )
        setInstitute( inst )
    }

    const handleBatchSubjectChange = ( event ) => {
        const selectedSubjects = subjects?.filter( s => event.target.value?.includes( s.subject_code ) )
        setBatchSubject( selectedSubjects )
    }

    const debouncedSubjectSearch = useCallback( debounce( "batch-subject-search", async filter => {
        setLoadingSubjects( true )
        if ( filter && filter.trim().length > 2 ) {
            try {
                const { data: { data } } = await api.audit.fetchSubjects( { subject_code: filter, institute: institute?.institute_name_short } )
                setSubjects( data )
            } catch ( err ) {
                setSubjects( [] )
                toast( err?.response?.data?.message || `Failed to fetch subjects for '${filter}'.` )
            }
        } else {
            setSubjects( [] )
        }

        setLoadingSubjects( false )
    }, 500 ), [institute] )

    const handleFilesSelected = ( fileList ) => {
        const accepted = ACCEPTED_VIDEO_TYPES.split( "," )
        const incoming = Array.from( fileList ).filter( file => {
            if ( !accepted.includes( file.type ) ) {
                toast( `Unsupported file: ${file.name}. Only .mp4, .mov and .webm are supported.` )
                return false
            }
            if ( file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024 ) {
                toast( `File too large: ${file.name}. Maximum size supported is ${getFileSizeFromBytes( MAX_VIDEO_SIZE_MB * 1024 * 1024 )}.` )
                return false
            }
            return true
        } )
        if ( incoming.length === 0 ) return
        const entries = incoming.map( file => createFileEntry( file, batchSubject, institute ) )
        setFiles( prev => [...prev, ...entries] )
        entries.forEach( entry => loadDuration( entry.id, entry.file ) )
    }

    const handleDrop = ( event ) => {
        event.preventDefault()
        setDragOver( false )
        handleFilesSelected( event.dataTransfer.files )
    }

    const canSubmit = files.length > 0 && files.every( entry => entry.name.trim() && entry.institute && entry.subjects && entry.module_number && entry?.topics_covered?.trim() )

    const handleSubmit = async () => {
        if ( !canSubmit ) return

        const cleanedFiles = []
        for ( const file of files ) {
            const { file: thumbnail, metadata } = await buildThumbnailFile( file )
            file.thumbnail = thumbnail
            file.thumbnail_metadata = metadata
            cleanedFiles.push( file )
        }

        UploadStore.addJobs( cleanedFiles )
        onClose()
    }

    useEffect( () => {
        OrganizationStore.fetchOrganization()
    }, [] )

    return (
        <Dialog open slotProps={{ paper: { sx: { maxWidth: "900px", width: "100%" } } }}>
            <DialogTitle sx={{ display: "flex", gap: "16px", alignItems: "flex-start", borderBottom: border[1], padding: "20px 24px 16px" }}>
                <Box flexGrow={1}>
                    <Typography fontSize="18px" fontWeight="600">Add recordings</Typography>
                    <Typography fontSize="13px" color="textSecondary" marginTop="5px">Select the video files and describe each one so students can find them</Typography>
                </Box>
                <IconButton onClick={onClose}>{Icons.default.CloseIcon}</IconButton>
            </DialogTitle>

            <Box sx={{ padding: layout.pagePadding, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ flexWrap: "wrap", display: "flex", alignItems: "flex-end", gap: 1 }}>
                    <Box>
                        <Typography component="label" htmlFor='batch-institute' sx={{ display: "block" }} variant='subtitle2' gutterBottom>Institute <Box component="span" color="secondary.main">*</Box></Typography>
                        <CustomSelectField
                            id="batch-institute"
                            disabled={files.length > 0}
                            renderOptionText={inst => <Box>
                                <Typography sx={{ fontSize: "14px", textTransform: "capitalize" }} >{inst.institute_name?.toLowerCase()}</Typography>
                                <CustomChip tooltipTitle='Institute Code' value={`${inst.institute_name_short}`} />
                            </Box>}
                            renderValue={instituteCode => {
                                const inst = OrganizationStore.getOrganization?.find( i => i.institute_name_short === instituteCode )
                                if ( !inst ) return instituteCode || "Select institute"
                                return (
                                    <CustomChip value={inst.institute_name_short} />
                                )
                            }}
                            itemValueKey="institute_name_short"
                            onChange={handleInstituteChange}
                            value={institute?.institute_name_short || ""}
                            data={OrganizationStore.getOrganization}
                            size="small"
                            fullWidth
                            selectStyles={{ width: "100%", maxWidth: 200 }}
                        />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography component="label" htmlFor='batch-subject' sx={{ display: "block" }} variant='subtitle2' gutterBottom>Select the subject these recordings belong to <Box component="span" color="secondary.main">*</Box></Typography>
                        <CustomSelectField
                            id="batch-subject"
                            disabled={files.length > 0}
                            renderOptionText={sub => <Box>
                                <Typography sx={{ fontSize: "14px", textTransform: "capitalize" }} >{sub.subject_name}</Typography>
                                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                                    <CustomChip tooltipTitle='Short Name' value={`${sub.subject_name_short}`} />
                                    <CustomChip tooltipTitle='Subject Code' value={`${sub.subject_code}`} />
                                    <CustomChip tooltipTitle='Academic Year' value={`${sub.ac_year}`} />
                                    <CustomChip tooltipTitle='Program Type' value={`${sub.program_type}`} />
                                    <CustomChip value={`Year: ${sub.year}`} />
                                    {sub.sem && <CustomChip value={`Sem: ${sub.sem}`} />}
                                </Box>
                            </Box>}
                            searchPlaceholder="Search by subject code..."
                            loadinOptionsMessage="Searching subjects..."
                            onSearch={debouncedSubjectSearch}
                            renderValue={( subjectCodes = [] ) => {
                                if ( subjectCodes.length === 0 ) return "Select subject"

                                const moreSubjectsList = subjectCodes.slice( 1 ).map( ( subjectCode, idx ) => {
                                    const subject = subjects.find( s => s.subject_code === subjectCode )
                                    if ( !subject )
                                        return <Typography key={subjectCode} component="span" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 500 }} >{`${idx + 2}. ${subjectCode}`}</Typography>
                                    return <Typography key={subjectCode} component="span" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 500 }} >{`${idx + 2}. ${subject.subject_name}`}</Typography>

                                } )

                                return <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                    {subjectCodes.slice( 0, 1 ).map( subjectCode => {
                                        const subject = subjects.find( s => s.subject_code === subjectCode )
                                        if ( !subject ) return subjectCode || "Select subject"
                                        return (
                                            <CustomChip
                                                key={subjectCode}
                                                value={subject.subject_code}
                                                tooltipTitle={subject.subject_name}
                                                noWrap
                                                textStyles={{ fontSize: "0.75rem" }}
                                            />
                                        )
                                    } )}
                                    {subjectCodes.length > 1 && <CustomChip
                                        tooltipTitle={<Box>
                                            {moreSubjectsList.map( s => s )}
                                        </Box>}
                                        value={`+${subjectCodes.length - 1} More`}
                                        noWrap
                                        textStyles={{ fontSize: "0.75rem" }}
                                    />}

                                </Box>
                            }}
                            multiple
                            loadingOptions={loadingSubjects}
                            itemValueKey="subject_code"
                            onChange={handleBatchSubjectChange}
                            value={batchSubject.map( sub => sub.subject_code )}
                            minSearchLength={3}
                            data={subjects}
                            size="small"
                            fullWidth
                        />
                    </Box>
                </Box>

                {batchSubject.length > 0 && <Box component="label" onDragOver={e => { e.preventDefault(); setDragOver( true ) }} onDragLeave={() => setDragOver( false )} onDrop={handleDrop}
                    sx={{
                        display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", cursor: "pointer", padding: "30px 20px", borderRadius: 5,
                        border: `1.5px dashed ${dragOver ? palette.secondary.main : palette.borderColor}`,
                        background: dragOver ? palette.contentBg : palette.form.formBg,
                        ":hover": {
                            borderColor: palette.primary.light,
                            bgcolor: palette.action.hover
                        }
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 50, aspectRatio: 1, pointerEvents: "none", userSelect: "none", fontSize: "1.6rem", p: 1, borderRadius: 10, bgcolor: palette.primary.light + "22", color: palette.primary.main }} >
                        {Icons.default.Upload}
                    </Box>
                    <Typography sx={{ pointerEvents: "none", userSelect: "none", }} fontSize="15px" fontWeight="600">Upload class recordings</Typography>
                    <Typography fontSize="13px" color="textSecondary" marginTop="6px"> <Typography sx={{ fontWeight: "500", fontSize: "inherit", color: "primary.main", display: "inline-block" }} component="span" >Click here</Typography>  to upload your files or drag and drop.</Typography>
                    <Typography sx={{ fontSize: "0.875rem", p: "5px 10px", width: "fit-content", fontWeight: 500, bgcolor: palette.secondary.main, color: palette.secondary.contrastText, zIndex: 0, mt: 1, borderRadius: 5, userSelect: "none" }}>Select from System</Typography>
                    <Typography sx={{ pointerEvents: "none", userSelect: "none" }} fontSize="12px" color="textSecondary" marginTop="6px">
                        Supports .mp4, .mov and .webm up to {getFileSizeFromBytes( MAX_VIDEO_SIZE_MB * 1024 * 1024 )} per file
                    </Typography>
                    <VisuallyHiddenInput type="file" multiple accept={ACCEPTED_VIDEO_TYPES} onChange={e => { handleFilesSelected( e.target.files ); e.target.value = "" }} />
                </Box>}

                {files.length > 0 && <Box >
                    <Box display="flex" alignItems="center" gap="10px" marginBottom="12px">
                        <Typography fontSize="13px" fontWeight="600">Selected videos</Typography>
                        <Typography component="span" fontSize="11.5px" fontWeight="600" padding="2px 9px" borderRadius="12px" bgcolor={palette.contentBg}>{files.length}</Typography>
                        <Button component="label" size="small" color="secondary" sx={{ marginLeft: "auto", textTransform: "capitalize" }}>
                            + Add more files
                            <VisuallyHiddenInput type="file" multiple accept={ACCEPTED_VIDEO_TYPES} onChange={e => { handleFilesSelected( e.target.files ); e.target.value = "" }} />
                        </Button>
                    </Box>
                    <Box display="flex" flexDirection="column" gap="12px">
                        {files.map( entry => (
                            <RecordingFileCard defaultSubject={batchSubject} key={entry.id} entry={entry} onPatch={patchFile} onRemove={removeFile} modules={modules} searchSubjects={searchSubjects} moduleNameMap={moduleNameMap} onModuleNameCommit={commitModuleName} />
                        ) )}
                    </Box>
                </Box>}
            </Box>

            <DialogActions sx={{ flexWrap: "wrap", gap: "12px", justifyContent: "space-between", borderTop: border[1], padding: "15px 24px", bgcolor: palette.contentBg }}>
                <Typography fontSize="12.5px" color="textSecondary">{files.length ? "Uploads continue in the background — you can keep working." : "No files selected yet."}</Typography>
                <Box display="flex" gap="10px">
                    <Button onClick={onClose} color="greyed" sx={{ textTransform: "capitalize" }}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit} variant="contained" disableElevation startIcon={Icons.default.CloudUpload} sx={{ textTransform: "capitalize" }}>
                        {files.length ? `Upload ${files.length} ${files.length === 1 ? "video" : "videos"}` : "Upload"}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
} )

export default AddRecordingDialog
